// react
import { useContext, useEffect, useState } from "react";
// libs
import { motion, AnimatePresence, Variants } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { useLockBodyScroll, useToggle } from "react-use";
import styled, { css } from "styled-components";
// components
import { MenuWrapper } from "./MenuWrapper";
import { Nav } from "./Nav";
import { Portal } from "../Portal";
import MenuFilters from "./MenuFilters";
import SearchHeader from "../SearchHeader";
import { Box } from "../Box";
import Feedback from "../feedback/Feedback";
// context
import { MenuOpenContext } from "../../context/MenuOpenContext";
// other
import {
  checkBoxStateToArr,
  handleCheckGroupChange,
  mapToInitialCheckGroupState,
} from "../../helpers/transformers/utils";
import { dateRanges, themeCategories } from "../../models/api";
import queryString from "query-string";
import { easings } from "../../styles/utils";

const MenuContainer = styled.div`
  max-width: 940px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;

  ${({ theme }) => css`
    ${theme.mediaQueries.md} {
      padding: 15vh 80px;
    }
  `}
`;

const dateInitialState = mapToInitialCheckGroupState(dateRanges);

const themeInitialState = mapToInitialCheckGroupState(themeCategories);

const navVariants: Variants = {
  initial: {
    y: -50,
    opacity: 0,
  },
  animate: {
    y: 0,
    opacity: 1,
    transition: { duration: 1.2, ease: easings.easeOutCubic },
  },
  exit: {
    y: -50,
    opacity: 0,
    transition: { duration: 0.3, ease: easings.easeOutCubic },
  },
};

function Menu() {
  const [showFilters, setShowFilters] = useToggle(false);
  const [feedbackOpen, toggleFeedbackOpen] = useToggle(false);

  const [dateFilters, setDateFilters] = useState(dateInitialState);
  const [themeFilters, setThemeFilters] = useState(themeInitialState);

  const [searchField, setSearchField] = useState<string>("");

  const navigate = useNavigate();
  const location = useLocation();

  const { menuOpen, setMenuOpen } = useContext(MenuOpenContext);

  const [locked, toggleLocked] = useToggle(false);

  const handleDatesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const keyCheckPair = {
      key: e.target.value,
      checked: e.target.checked,
    };
    setDateFilters(handleCheckGroupChange(dateFilters, keyCheckPair));
  };

  const handleThemesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const keyCheckPair = {
      key: e.target.value,
      checked: e.target.checked,
    };
    setThemeFilters(handleCheckGroupChange(themeFilters, keyCheckPair));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchField(e.target.value);
  };

  const handleSubmit = () => {
    const query = {
      q: searchField,
      d: checkBoxStateToArr(dateFilters),
      t: checkBoxStateToArr(themeFilters),
    };
    const qs = queryString.stringify(query, { arrayFormat: "comma" });

    setMenuOpen(false);
    setTimeout(() => {
      navigate(`/search?${qs}`, { state: { search: searchField } });
    }, 0.6 * 1000);
  };

  useLockBodyScroll(locked);

  useEffect(() => {
    toggleLocked(menuOpen);
  }, [menuOpen, toggleLocked]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location, setMenuOpen]);

  return (
    <Portal>
      <AnimatePresence>
        {menuOpen && (
          <MenuWrapper key="menu-wrapper">
            <MenuContainer>
              <motion.div variants={navVariants}>
                <SearchHeader
                  searchInputValue={searchField}
                  onSearchInputChange={handleSearchChange}
                  onSubmitSearch={handleSubmit}
                  onToggleFilterClick={setShowFilters}
                />
                <Box pt={5}>
                  <AnimatePresence exitBeforeEnter>
                    {!showFilters ? (
                      <Nav key="navvv" />
                    ) : (
                      <MenuFilters
                        key="filteraas"
                        dateState={dateFilters}
                        themeState={themeFilters}
                        onDatesChange={handleDatesChange}
                        onThemesChange={handleThemesChange}
                        onSubmit={handleSubmit}
                      />
                    )}
                  </AnimatePresence>
                </Box>
              </motion.div>
            </MenuContainer>
            <Feedback open={feedbackOpen} onToggleClick={toggleFeedbackOpen} />
          </MenuWrapper>
        )}
      </AnimatePresence>
    </Portal>
  );
}

export default Menu;
